import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AgentChat from "../../components/AgentChat/AgentChat";
import RulesContext from "../../context/rules-context";
import {
    buildCompanyEventPayload,
    collectRulePieceKeys,
    fetchRulePieceDefinitions
} from "./RuleTree/ruleAgentClient";
import { translateOutcomeTreeData } from "./RuleTree/treeUtils";
import { useRuleArtifactRenderers } from "./ruleArtifactRenderer";

/* Everything the dash agent needs to know about rules is assembled here and handed to the
   generic AgentChat, which stays unaware of this domain. When another screen of the dashboard
   gains its own skill, it writes its own adapter beside this one — none of them touch the chat.

   The `text` of a context entry is the NAME OF A SLOT, not a sentence about it. The cell resolves a
   slot by comparing this string exactly (`find_context_entry` in the agent's
   integrations/risk-solutions-dash/tools/context_entries.py) and keeps only the newest row of each
   slot in what the model reads, so a label that varies — with the rule's name, say — opens a second
   slot instead of replacing the first, and leaves the agent reading a stale snapshot. The first three
   below mirror that file literally and must not drift from it; the fourth is ours alone, since no
   tool of the agent resolves a referenced piece by name.

   What describes an entry to the model travels in `data`: the agent's motor-de-regras skill already
   says what arrives in each slot. They stay in Portuguese for the same reason the prompts in
   ruleAgentClient.js do — they are read by the model, not by the user. */

const RULE_IN_EDITION_LABEL = "Regra atual em edição";
const RULE_PIECE_IN_EDITION_LABEL = "Trecho de regra em edição";
const COMPANY_EVENT_CONTEXT_LABEL = "Contexto do evento da empresa";
/* One slot per referenced piece, keyed by the piece and not by its name: renaming a piece mid-chat
   must refresh its slot, not open a second one beside it. */
const REFERENCED_RULE_PIECE_LABEL = "Trecho de regra referenciado: ";

function buildRuleEntry(ruleData) {
    return {
        text: ruleData.is_rule_piece ? RULE_PIECE_IN_EDITION_LABEL : RULE_IN_EDITION_LABEL,
        data: ruleData
    };
}

function buildCompanyEventEntry(payload) {
    return { text: COMPANY_EVENT_CONTEXT_LABEL, data: payload };
}

function buildRulePieceEntry(definition) {
    return {
        text: REFERENCED_RULE_PIECE_LABEL + definition.rule_piece_key,
        data: definition
    };
}

function RuleEngineAgentChat({
    treeData,
    userVariablesAvailable,
    ruleName,
    agentContext,
    onInsertGeneratedNodes,
    selectionAttachments,
    onSelectionAttachmentsChange,
    open,
    onPanelOpenChange
}) {
    const { t } = useTranslation();
    const rulesContext = useContext(RulesContext);

    const isRulePiece = Boolean(rulesContext.isRulePiece);
    const environment = rulesContext.environment;
    const companyKey = rulesContext.company_key;

    /* Serialized so everything downstream keys off the CONTENT of the rule, not the identity of
       a freshly built object. That is what keeps an unchanged rule from being re-sent with every
       message — the same dedup the old client did with lastSentRuleJsonRef. */
    const ruleDataJson = useMemo(() => {
        let data = { tree_data: translateOutcomeTreeData(treeData) };
        if (userVariablesAvailable && userVariablesAvailable.length > 0) {
            data.user_variables = userVariablesAvailable;
        }
        if (ruleName) data.name = ruleName;
        if (isRulePiece) data.is_rule_piece = true;
        if (agentContext) data.agent_context = agentContext;
        return JSON.stringify(data);
    }, [treeData, userVariablesAvailable, ruleName, isRulePiece, agentContext]);

    const [rulePieceEntries, setRulePieceEntries] = useState([]);

    useEffect(() => {
        let keys = collectRulePieceKeys(JSON.parse(ruleDataJson).tree_data);
        if (keys.length === 0) {
            setRulePieceEntries([]);
            return undefined;
        }
        let cancelled = false;
        fetchRulePieceDefinitions({ keys: keys, environment: environment, companyKey: companyKey })
            .then((definitions) => {
                if (cancelled) return;
                setRulePieceEntries(definitions.map(buildRulePieceEntry));
            });
        return () => {
            cancelled = true;
        };
    }, [ruleDataJson, environment, companyKey]);

    const turnContext = useMemo(
        () => [buildRuleEntry(JSON.parse(ruleDataJson))].concat(rulePieceEntries),
        [ruleDataJson, rulePieceEntries]
    );

    const chatContext = useMemo(() => {
        let payload = buildCompanyEventPayload(rulesContext);
        return payload ? [buildCompanyEventEntry(payload)] : [];
    }, [rulesContext]);

    const renderers = useRuleArtifactRenderers({
        rulesContext: rulesContext,
        userVariablesAvailable: userVariablesAvailable,
        onInsertNodes: onInsertGeneratedNodes
    });

    const isEmptyTree = !treeData || treeData.length === 0;
    const suggestions = useMemo(
        () =>
            isEmptyTree
                ? [
                    t("Sugira validações comuns para este tipo de evento"),
                    t("Monte uma estrutura inicial para esta regra"),
                    t("Quais campos do evento posso usar?"),
                    t("Quais dados externos posso consultar?")
                ]
                : [
                    t("O que esta regra faz?"),
                    t("Explique a última alteração"),
                    t("Há algum problema nesta regra?"),
                    t("Como simplificar esta regra?")
                ],
        [isEmptyTree, t]
    );

    return (
        <AgentChat
            chatContext={chatContext}
            turnContext={turnContext}
            renderers={renderers}
            attachments={selectionAttachments}
            onAttachmentsChange={onSelectionAttachmentsChange}
            open={open}
            onPanelOpenChange={onPanelOpenChange}
            /* The editor is where a rule is worked on, not a place a conversation is kept: a
               reload means a new session of work, and resuming yesterday's thread about another
               rule would be noise. A chat already started survives closing and reopening. */
            alwaysNewChat
            title={t("Assistente de Regras")}
            greeting={t("Olá! Como posso ajudar com esta regra?")}
            emptySubtitle={
                isEmptyTree
                    ? t("Peça sugestões ou gere validações para começar a montar sua regra.")
                    : t("Tire dúvidas ou peça análises sobre a regra que você está editando.")
            }
            suggestions={suggestions}
        />
    );
}

export default RuleEngineAgentChat;
